import React, { useState } from "react";
import Layout from "../components/Layout";

function AdminFeatures() {
  const [completedSteps, setCompletedSteps] = useState(new Set());
  const [selectedCategory, setSelectedCategory] = useState("all");

  const toggleStep = (stepId) => {
    const newCompleted = new Set(completedSteps);
    if (newCompleted.has(stepId)) {
      newCompleted.delete(stepId);
    } else {
      newCompleted.add(stepId);
    }
    setCompletedSteps(newCompleted);
  };

  const adminCategories = [
    {
      id: "account-setup",
      name: "Account Management",
      icon: "bi-people",
      color: "primary",
      description: "Manage job coaches and staff accounts",
    },
    {
      id: "consumer-setup",
      name: "Consumer Setup",
      icon: "bi-person-plus",
      color: "success",
      description: "Add and configure consumer accounts",
    },
    {
      id: "billing-invoicing",
      name: "Billing & Invoicing",
      icon: "bi-receipt",
      color: "warning",
      description: "Generate invoices and track payments",
    },
    {
      id: "reporting-analytics",
      name: "Reporting & Analytics",
      icon: "bi-graph-up",
      color: "info",
      description: "Dashboard insights and custom reports",
    },
    {
      id: "system-settings",
      name: "System Settings",
      icon: "bi-gear",
      color: "danger",
      description: "Configure organizational preferences",
    },
  ];

  const adminTrainingSteps = [
    {
      id: "admin-dashboard",
      title: "Understanding the Admin Dashboard",
      description: "Master the central command center for your organization",
      category: "account-setup",
      estimatedTime: "10 minutes",
      difficulty: "Beginner",
      icon: "bi-speedometer2",
      color: "primary",
      details: [
        "Navigate the admin-specific dashboard widgets",
        "Monitor organization-wide activity and alerts",
        "Understand key metrics and performance indicators",
        "Customize your dashboard layout and preferences",
      ],
      videoPlaceholder: true,
      importance: "Essential",
    },
    {
      id: "user-roles",
      title: "Creating a New User & Permissions",
      description: "Setting up a new job coaches",
      category: "account-setup",
      estimatedTime: "5 minutes",
      difficulty: "Beginner",
      icon: "bi-shield-check",
      color: "primary",
      details: [
        "Create new user accounts for job coaches",
        "Assign appropriate role-based permissions",
        "Configure data access restrictions",
        "Manage password policies and security settings",
      ],
      videoPlaceholder: true,
      importance: "Critical",
    },
    {
      id: "consumer-onboarding",
      title: "Consumer Account Setup",
      description: "Add new consumers and configure their profiles",
      category: "consumer-setup",
      estimatedTime: "12 minutes",
      difficulty: "Beginner",
      icon: "bi-person-plus-fill",
      color: "success",
      details: [
        "Create comprehensive consumer profiles",
        "Set up authorization details and hour limits",
        "Configure service types and billing rates",
        "Assign job coaches to specific consumers",
      ],
      videoPlaceholder: true,
      importance: "Essential",
    },
    {
      id: "authorization-management",
      title: "Authorization Tracking & Management",
      description: "Monitor and manage service authorizations",
      category: "consumer-setup",
      estimatedTime: "18 minutes",
      difficulty: "Intermediate",
      icon: "bi-clipboard-check",
      color: "success",
      details: [
        "Set up new authorizations with proper limits",
        "Monitor hour usage and remaining balances",
        "Configure automated alerts for authorization limits",
        "Manage authorization renewals and extensions",
      ],
      videoPlaceholder: true,
      importance: "Critical",
    },
    {
      id: "invoice-generation",
      title: "Professional Invoice Generation",
      description: "Create and manage billing documentation",
      category: "billing-invoicing",
      estimatedTime: "20 minutes",
      difficulty: "Advanced",
      icon: "bi-receipt-cutoff",
      color: "warning",
      details: [
        "Generate monthly invoices with supporting documentation",
        "Customize invoice templates and branding",
        "Review and approve billable hours before invoicing",
        "Export invoices in multiple formats (PDF, Excel)",
      ],
      videoPlaceholder: true,
      importance: "Critical",
    },
    {
      id: "billing-rates",
      title: "Billing Rates & Financial Setup",
      description: "Configure rates, customers, and financial settings",
      category: "billing-invoicing",
      estimatedTime: "15 minutes",
      difficulty: "Intermediate",
      icon: "bi-currency-dollar",
      color: "warning",
      details: [
        "Set up customer accounts and billing addresses",
        "Configure service rates for different authorization types",
        "Manage mileage rates and reimbursement policies",
        "Set up automated billing calculations",
      ],
      videoPlaceholder: true,
      importance: "Essential",
    },
    {
      id: "monthly-reporting",
      title: "Monthly Reports & Analytics",
      description: "Generate and customize organizational reports",
      category: "reporting-analytics",
      estimatedTime: "16 minutes",
      difficulty: "Intermediate",
      icon: "bi-file-earmark-bar-graph",
      color: "info",
      details: [
        "Generate automated monthly progress reports",
        "Create custom reports for specific time periods",
        "Export reports for state agencies and customers",
        "Set up automated report delivery schedules",
      ],
      videoPlaceholder: true,
      importance: "Essential",
    },
    {
      id: "compliance-monitoring",
      title: "Compliance & Audit Tracking",
      description: "Ensure documentation meets regulatory requirements",
      category: "reporting-analytics",
      estimatedTime: "14 minutes",
      difficulty: "Advanced",
      icon: "bi-shield-fill-check",
      color: "info",
      details: [
        "Monitor compliance with state and federal requirements",
        "Generate audit-ready documentation packages",
        "Track missing signatures and incomplete documentation",
        "Set up compliance alerts and notifications",
      ],
      videoPlaceholder: true,
      importance: "Critical",
    },
    {
      id: "organization-settings",
      title: "Organization Configuration",
      description: "Customize system settings for your organization",
      category: "system-settings",
      estimatedTime: "12 minutes",
      difficulty: "Advanced",
      icon: "bi-building-gear",
      color: "danger",
      details: [
        "Configure organizational branding and logos",
        "Set up default preferences and workflows",
        "Manage data retention and backup policies",
        "Configure integration settings with external systems",
      ],
      videoPlaceholder: true,
      importance: "Important",
    },
  ];

  const filteredSteps = adminTrainingSteps.filter(
    (step) => selectedCategory === "all" || step.category === selectedCategory
  );

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case "Beginner":
        return "success";
      case "Intermediate":
        return "warning";
      case "Advanced":
        return "danger";
      default:
        return "secondary";
    }
  };

  const getImportanceColor = (importance) => {
    switch (importance) {
      case "Critical":
        return "danger";
      case "Essential":
        return "warning";
      case "Important":
        return "info";
      default:
        return "secondary";
    }
  };

  // Inline styles object
  const styles = {
    moduleIcon: {
      width: "45px",
      height: "45px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "1.1rem",
    },
    videoPlaceholder: {
      minHeight: "120px",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
    },
    moduleCard: {
      position: "relative",
    },
    bgGradientWarning: {
      background: "linear-gradient(135deg, #ffc107 0%, #fd7e14 100%)",
    },
    tipItem: {
      borderLeft: "3px solid transparent",
      paddingLeft: "0.5rem",
    },
    tipItem1: {
      borderLeftColor: "#ffc107",
    },
    tipItem2: {
      borderLeftColor: "#198754",
    },
    tipItem3: {
      borderLeftColor: "#0dcaf0",
    },
  };

  return (
    <Layout>
      <div className="min-vh-100 d-flex flex-column">
        {/* Navbar */}
        {/* <nav className="navbar navbar-expand-lg navbar-dark bg-dark sticky-top">
        <div className="container">
          <a className="navbar-brand fw-bold" href="/">
            <i className="bi bi-layers me-2"></i>
            WebVenly
          </a>
          <div className="d-flex gap-2">
            <a href="/videos" className="btn btn-outline-light btn-sm">
              <i className="bi bi-arrow-left me-2"></i>
              All Training
            </a>
            <a href="/gettingstarted" className="btn btn-outline-light btn-sm">
              Getting Started
            </a>
          </div>
        </div>
      </nav> */}

        {/* Hero Section */}
        <section className="text-white py-5" style={styles.bgGradientWarning}>
          <div className="container">
            <div className="row align-items-center">
              <div className="col-lg-8 mx-auto text-center">
                <h1 className="display-4 fw-bold mb-4">
                  <i className="bi bi-gear me-3"></i>
                  Admin Features Training
                </h1>
                <p className="lead mb-4">
                  Master the administrative tools that transform your CRP into a
                  professional operation
                </p>
                <div className="d-flex justify-content-center gap-3 flex-wrap">
                  <div className="badge bg-warning bg-opacity-75 px-3 py-2">
                    <i className="bi bi-people me-2"></i>
                    User Management
                  </div>
                  <div className="badge bg-light text-dark px-3 py-2">
                    <i className="bi bi-graph-up me-2"></i>
                    Analytics & Reports
                  </div>
                  <div className="badge bg-primary bg-opacity-75 px-3 py-2">
                    <i className="bi bi-shield-check me-2"></i>
                    Compliance Ready
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Progress Overview */}
        {/* <section className="py-4 bg-light">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-md-4">
              <h5 className="mb-0">Training Progress</h5>
              <small className="text-muted">
                {completedSteps.size} of {adminTrainingSteps.length} modules
                completed
              </small>
            </div>
            <div className="col-md-4">
              <div className="progress">
                <div
                  className="progress-bar bg-warning"
                  style={{
                    width: `${
                      (completedSteps.size / adminTrainingSteps.length) * 100
                    }%`,
                  }}
                ></div>
              </div>
            </div>
            <div className="col-md-4 text-md-end">
              <small className="text-muted">
                <i className="bi bi-clock me-1"></i>
                ~2.5 hours total training time
              </small>
            </div>
          </div>
        </div>
      </section> */}

        {/* Category Filter */}
        <section className="py-3 border-bottom">
          <div className="container">
            <div className="d-flex gap-2 flex-wrap">
              <button
                className={`btn btn-sm ${
                  selectedCategory === "all"
                    ? "btn-warning"
                    : "btn-outline-warning"
                }`}
                onClick={() => setSelectedCategory("all")}
              >
                All Modules
              </button>
              {adminCategories.map((category) => (
                <button
                  key={category.id}
                  className={`btn btn-sm ${
                    selectedCategory === category.id
                      ? `btn-${category.color}`
                      : `btn-outline-${category.color}`
                  }`}
                  onClick={() => setSelectedCategory(category.id)}
                >
                  <i className={`${category.icon} me-1`}></i>
                  {category.name}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-5">
          <div className="container">
            <div className="row">
              {/* Training Modules */}
              <div className="col-lg-8">
                <div className="training-modules">
                  {filteredSteps.map((step, index) => (
                    <div
                      key={step.id}
                      className="mb-4"
                      style={styles.moduleCard}
                    >
                      <div
                        className={`card border-${step.color} ${
                          completedSteps.has(step.id)
                            ? "bg-success bg-opacity-10"
                            : ""
                        }`}
                      >
                        <div className="card-header d-flex align-items-center justify-content-between">
                          <div className="d-flex align-items-center">
                            <div
                              className={`bg-${step.color} text-white rounded-circle me-3`}
                              style={styles.moduleIcon}
                            >
                              {completedSteps.has(step.id) ? (
                                <i className="bi bi-check-lg"></i>
                              ) : (
                                <i className={step.icon}></i>
                              )}
                            </div>
                            <div>
                              <h5 className="mb-0">{step.title}</h5>
                              <div className="d-flex gap-2 mt-1">
                                <span
                                  className={`badge bg-${getDifficultyColor(
                                    step.difficulty
                                  )}`}
                                >
                                  {step.difficulty}
                                </span>
                                <span
                                  className={`badge bg-${getImportanceColor(
                                    step.importance
                                  )}`}
                                >
                                  {step.importance}
                                </span>
                                <small className="text-muted">
                                  <i className="bi bi-clock me-1"></i>
                                  {step.estimatedTime}
                                </small>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="card-body">
                          <p className="card-text mb-3">{step.description}</p>

                          {/* Video Content */}
                          {step.id === "user-roles" ? (
                            <div className="video-container mb-3">
                              <div className="ratio ratio-16x9">
                                <iframe
                                  src="https://www.youtube.com/embed/qZeW0rkAgkk"
                                  title="How to Create a New User in WebVenly"
                                  allowFullScreen
                                  className="rounded-3"
                                ></iframe>
                              </div>
                              <div className="mt-2 d-flex justify-content-between align-items-center">
                                <small className="text-muted">
                                  <i className="bi bi-play-circle me-1"></i>
                                  How to Create a New User in WebVenly
                                </small>
                                <a
                                  href="https://youtu.be/qZeW0rkAgkk"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="btn btn-sm btn-outline-primary"
                                >
                                  <i className="bi bi-box-arrow-up-right me-1"></i>
                                  Watch on YouTube
                                </a>
                              </div>
                            </div>
                          ) : (
                            step.videoPlaceholder && (
                              <div
                                className="bg-light rounded-3 p-4 mb-3 text-center border border-2 border-dashed"
                                style={styles.videoPlaceholder}
                              >
                                <i
                                  className={`bi bi-camera-video text-${step.color} fs-1 mb-2`}
                                ></i>
                                <p className="mb-2 fw-semibold">
                                  Training Video Coming Soon
                                </p>
                                <small className="text-muted">
                                  Comprehensive video guide for "{step.title}"
                                </small>
                              </div>
                            )
                          )}

                          {/* Module Details */}
                          <div className="module-details">
                            <h6 className="fw-semibold mb-2">
                              What you'll master:
                            </h6>
                            <ul className="list-unstyled">
                              {step.details.map((detail, detailIndex) => (
                                <li
                                  key={detailIndex}
                                  className="d-flex align-items-start mb-2"
                                >
                                  <i
                                    className={`bi bi-arrow-right text-${step.color} me-2 mt-1`}
                                  ></i>
                                  <span className="small">{detail}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Sidebar */}
              <div className="col-lg-4">
                {/* Admin Quick Reference */}
                <div className="card mb-4">
                  <div className="card-header">
                    <h6 className="mb-0">
                      <i className="bi bi-bookmark me-2"></i>
                      Admin Quick Reference
                    </h6>
                  </div>
                  <div className="card-body">
                    <div className="admin-tips">
                      <div
                        className="tip-item mb-3"
                        style={{ ...styles.tipItem, ...styles.tipItem1 }}
                      >
                        <div className="d-flex align-items-start">
                          <i className="bi bi-lightbulb text-warning fs-5 me-2"></i>
                          <div>
                            <strong className="small">Best Practice</strong>
                            <p className="small text-muted mb-0">
                              Always test changes in TRN environment before
                              applying to PRD
                            </p>
                          </div>
                        </div>
                      </div>
                      <div
                        className="tip-item mb-3"
                        style={{ ...styles.tipItem, ...styles.tipItem2 }}
                      >
                        <div className="d-flex align-items-start">
                          <i className="bi bi-shield-check text-success fs-5 me-2"></i>
                          <div>
                            <strong className="small">Security Tip</strong>
                            <p className="small text-muted mb-0">
                              Review user permissions quarterly and remove
                              inactive accounts
                            </p>
                          </div>
                        </div>
                      </div>
                      <div
                        className="tip-item"
                        style={{ ...styles.tipItem, ...styles.tipItem3 }}
                      >
                        <div className="d-flex align-items-start">
                          <i className="bi bi-graph-up text-info fs-5 me-2"></i>
                          <div>
                            <strong className="small">Efficiency Tip</strong>
                            <p className="small text-muted mb-0">
                              Set up automated reports to save time on monthly
                              submissions
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Categories Overview */}
                <div className="card mb-4">
                  <div className="card-header">
                    <h6 className="mb-0">
                      <i className="bi bi-list-ul me-2"></i>
                      Training Categories
                    </h6>
                  </div>
                  <div className="card-body p-4">
                    {adminCategories.map((category) => (
                      <div
                        key={category.id}
                        className={`list-group-item list-group-item-action border-0 ${
                          selectedCategory === category.id ? "active" : ""
                        }`}
                        style={{ cursor: "pointer" }}
                        onClick={() => setSelectedCategory(category.id)}
                      >
                        <div className="d-flex align-items-start">
                          <i
                            className={`${category.icon} text-${category.color} fs-5 me-3 mt-1`}
                          ></i>
                          <div>
                            <div className="fw-semibold">{category.name}</div>
                            <small className="text-muted">
                              {category.description}
                            </small>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Support Resources */}
                <div className="card">
                  <div className="card-header">
                    <h6 className="mb-0">
                      <i className="bi bi-headset me-2"></i>
                      Need Help?
                    </h6>
                  </div>
                  <div className="card-body">
                    <div className="d-grid gap-2">
                      <a href="/demo" className="btn btn-warning btn-sm">
                        <i className="bi bi-calendar-check me-2"></i>
                        Schedule Admin Demo
                      </a>
                      <a
                        href="/contact"
                        className="btn btn-outline-warning btn-sm"
                      >
                        <i className="bi bi-envelope me-2"></i>
                        Contact Support
                      </a>
                    </div>
                    <hr />
                    <small className="text-muted">
                      <i className="bi bi-telephone me-1"></i>
                      Quick help: 681-363-0183
                    </small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-dark text-white py-4 mt-auto">
          <div className="container text-center">
            <p className="mb-1">
              &copy; {new Date().getFullYear()} WebVenly. All rights reserved.
            </p>
            <p className="mb-0 small text-light">
              Powered by <strong>Wyatt Multi Solutions</strong>
            </p>
          </div>
        </footer>
      </div>
    </Layout>
  );
}

export default AdminFeatures;
